import type { FloorPlanSketch, SketchInk, SketchShape } from '../models/floorplan-sketch';
import { asDrawnShapesOf } from './as-drawn';
import {
  inkOf, traceDrawing, traceSketch, type GreyImage, type TraceOptions,
} from './image-trace';
import {
  TRACE_LONGEST_SIDE, drawFramed, framingOf, greyOfRgba,
} from './picture-prep';
import {
  PICTURE_TYPES, headOf, pictureFormatOf, tiffOf, type PictureFormat,
} from './picture-format';
import type { TiffPicture } from './tiff';
import type { ReadingStage, TraceReply, TraceRequest } from './trace-messages';
import { createTraceWorker } from './trace-worker';

/**
 * Getting an uploaded picture read: opened, scaled, made grey and traced — all in the browser.
 *
 * Where the browser can (`createImageBitmap` and `OffscreenCanvas` in a background thread, as
 * every current browser has), all of it happens in the background thread and the page only
 * waits, told how far it has got. Where it cannot, the page opens and greys the picture itself,
 * as it always did, and hands the pixels over to be traced; and with no background thread at all,
 * everything happens on the page. The result is the same whichever way it went.
 */

export { TRACE_LONGEST_SIDE };

export interface LoadedImage {
  readonly source: CanvasImageSource;
  readonly width: number;
  readonly height: number;
}

/** Why a reading ended without a sketch because the file is no picture this browser can open. */
export const IMAGE_UNREADABLE = 'image-unreadable';

/** A file this browser cannot open as a picture: what it seems to be, so the page can say what to do. */
export class PictureUnreadable extends Error {
  constructor(readonly format: PictureFormat, readonly reason?: string) {
    super(IMAGE_UNREADABLE);
    this.name = 'PictureUnreadable';
  }
}

/** Why a trace ended without a sketch because nobody wanted it any more. */
export const TRACE_CANCELLED = 'trace-cancelled';

/**
 * The largest canvas the page makes for a TIFF, in pixels: well inside what every browser allows
 * (Safari's is the smallest, at about 16.7 million), so a canvas is never silently left blank.
 */
export const PAGE_CANVAS_AREA = 16 * 1024 * 1024;

/**
 * Four-byte pixels made smaller by averaging each block of them: the page's own way to shrink a
 * decoded TIFF, with no canvas the size of the whole picture.
 */
const shrunk = (rgba: Uint8ClampedArray, width: number, height: number, toWidth: number, toHeight: number): Uint8ClampedArray => {
  const out = new Uint8ClampedArray(toWidth * toHeight * 4);
  for (let y = 0; y < toHeight; y += 1) {
    const top = Math.floor((y * height) / toHeight);
    const bottom = Math.max(top + 1, Math.floor(((y + 1) * height) / toHeight));
    for (let x = 0; x < toWidth; x += 1) {
      const left = Math.floor((x * width) / toWidth);
      const right = Math.max(left + 1, Math.floor(((x + 1) * width) / toWidth));
      const sums = [0, 0, 0, 0];
      for (let row = top; row < bottom; row += 1) {
        for (let column = left; column < right; column += 1) {
          const at = ((row * width) + column) * 4;
          sums[0] += rgba[at];
          sums[1] += rgba[at + 1];
          sums[2] += rgba[at + 2];
          sums[3] += rgba[at + 3];
        }
      }
      const count = (bottom - top) * (right - left);
      out.set(sums.map((sum) => sum / count), ((y * toWidth) + x) * 4);
    }
  }
  return out;
};

/**
 * A decoded TIFF made into something the page can draw, no larger than the tracer reads: scaled by
 * the browser where it can, averaged here where it cannot, and never on a canvas the size of a
 * large scan.
 */
const tiffForThePage = async (tiff: TiffPicture): Promise<LoadedImage> => {
  const factor = Math.min(1, TRACE_LONGEST_SIDE / Math.max(tiff.width, tiff.height));
  const width = Math.max(1, Math.round(tiff.width * factor));
  const height = Math.max(1, Math.round(tiff.height * factor));

  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(new ImageData(tiff.pixels, tiff.width, tiff.height), {
        resizeWidth: width, resizeHeight: height, resizeQuality: 'high',
      });
      // A browser that ignores the size asked for is no help here.
      if (bitmap.width === width && bitmap.height === height) return { source: bitmap, width, height };
      bitmap.close();
    } catch {
      // Scaled here instead.
    }
  }

  if (width * height > PAGE_CANVAS_AREA) throw new Error('the picture is too large to open on this page');
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('this browser cannot draw the picture');
  const pixels = factor < 1 ? shrunk(tiff.pixels, tiff.width, tiff.height, width, height) : tiff.pixels;
  context.putImageData(new ImageData(pixels, width, height), 0, 0);
  return { source: canvas, width, height };
};

/** The picture in a file, opened on the page: by the browser, or — a TIFF — here. */
export const loadImageFile = async (file: Blob): Promise<LoadedImage> => {
  const format = pictureFormatOf(await headOf(file));

  if (format === 'tiff') {
    try {
      return await tiffForThePage(await tiffOf(file));
    } catch (error) {
      throw new PictureUnreadable(format, (error as Error)?.message);
    }
  }

  // Told what it really is, whatever the file was called: an image element opens an SVG only when
  // it is typed as one.
  const type = PICTURE_TYPES[format];
  const typed = type && file.type !== type ? new Blob([file], { type }) : file;

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(typed);
    const image = new Image();

    image.onload = (): void => {
      URL.revokeObjectURL(url);
      if (!image.naturalWidth || !image.naturalHeight) reject(new PictureUnreadable(format));
      else resolve({ source: image, width: image.naturalWidth, height: image.naturalHeight });
    };

    image.onerror = (): void => {
      URL.revokeObjectURL(url);
      reject(new PictureUnreadable(format));
    };

    image.src = url;
  });
};

/** The image drawn on a canvas of the page's, scaled, turned `degrees` clockwise, on white. */
const drawn = (image: LoadedImage, longestSide: number, degrees = 0): CanvasRenderingContext2D => {
  const framing = framingOf(image.width, image.height, longestSide, degrees);
  const canvas = document.createElement('canvas');
  canvas.width = framing.width;
  canvas.height = framing.height;

  const context = canvas.getContext('2d') as CanvasRenderingContext2D;
  drawFramed(context, image.source, framing);
  return context;
};

/** The image in shades of grey, at the size the tracer works at, turned `degrees` clockwise. */
export const imageForTracing = (image: LoadedImage, degrees = 0): GreyImage => {
  const context = drawn(image, TRACE_LONGEST_SIDE, degrees);
  const { width, height } = context.canvas;
  return greyOfRgba(context.getImageData(0, 0, width, height).data, width, height);
};

/**
 * The picture shown under what was found in review: the same size and turn as what was traced,
 * so the two line up. Empty when the browser cannot make one.
 */
export const previewForReview = (image: LoadedImage, degrees = 0): string => {
  try {
    return drawn(image, TRACE_LONGEST_SIDE, degrees).canvas.toDataURL('image/jpeg', 0.85);
  } catch {
    return '';
  }
};

/**
 * The page's own way of opening and greying a picture, for when the background thread cannot:
 * one object, so a test can stand in for the canvas the test runner does not have.
 */
export const pageImaging = {
  load: loadImageFile,
  grey: imageForTracing,
  preview: previewForReview,
};

/**
 * How long the background thread may say nothing before it is taken to have stopped. It says
 * something at every step, and the slowest step of a large photo takes a fraction of a second
 * (`npm run trace:benchmark`): twenty seconds of silence is a thread that has stopped, on any
 * machine.
 */
export const TRACE_SILENCE_MS = 20000;

/** How far a reading has got: its step, which reading, and roughly how much of it is done, 0 to 1. */
export interface ReadingProgress {
  readonly stage: ReadingStage;
  readonly reading: 1 | 2;
  readonly share: number;
}

const STAGES: readonly ReadingStage[] = [
  'opening', 'straighten', 'parts', 'directions', 'spaces', 'building', 'doors', 'objects', 'rooms', 'walls', 'writing', 'furniture',
  'openings',
];
const FIRST_OF_A_READING = STAGES.indexOf('spaces');

/** The share done at the start of `stage`: the first reading to four fifths, a second the rest. */
export const progressOf = (stage: ReadingStage, reading: 1 | 2): ReadingProgress => {
  const index = Math.max(0, STAGES.indexOf(stage));
  const share = reading === 2
    ? 0.8 + ((0.2 * (index - FIRST_OF_A_READING)) / (STAGES.length - FIRST_OF_A_READING))
    : (0.8 * index) / STAGES.length;
  return { stage, reading, share: Math.round(share * 100) / 100 };
};

/** What the page says of each step of a reading, as it happens: a translation key. */
export const readingStepKeyOf = (progress: ReadingProgress): string => {
  if (progress.reading === 2) return 'floorplan-editor-image-progress-checking';
  if (progress.stage === 'opening') return 'floorplan-editor-image-progress-opening';
  if (progress.stage === 'straighten' || progress.stage === 'parts' || progress.stage === 'directions') {
    return 'floorplan-editor-image-progress-lines';
  }
  return 'floorplan-editor-image-progress-finding';
};

/** A file's contents as a `data:` URL: a picture the page can show, with nothing to release after. */
const dataUrlOf = (blob: Blob): Promise<string> => new Promise((resolve) => {
  const reader = new FileReader();
  reader.onload = (): void => resolve(typeof reader.result === 'string' ? reader.result : '');
  reader.onerror = (): void => resolve('');
  reader.readAsDataURL(blob);
});

export interface PictureReading {
  readonly sketch: FloorPlanSketch;
  /** The drawing's own ink, to keep what nobody marks as drawn; null when no building was found. */
  readonly ink: SketchInk | null;
  /** The picture as it was read, to show under the finds; empty when there is none. */
  readonly previewUrl: string;
  /** The size of the picture as it was read: scaled, and turned. */
  readonly width: number;
  readonly height: number;
  /** How long each part took, in milliseconds, where the background thread timed it. */
  readonly timings: Readonly<Record<string, number>>;
}

export interface ReadingOptions {
  /** Turned this far clockwise first. */
  readonly degrees?: number;
  readonly signal?: AbortSignal;
  readonly onProgress?: (progress: ReadingProgress) => void;
  readonly options?: TraceOptions;
}

/** A background thread to trace on, or none when the browser has none or it cannot start. */
const startWorker = (): Worker | undefined => {
  if (typeof Worker === 'undefined') return undefined;
  try {
    return createTraceWorker();
  } catch {
    return undefined;
  }
};

/**
 * The picture in `file` read into a sketch, off the page wherever the browser allows.
 *
 * `signal` stops it, wherever it has got to: the thread is ended and the promise rejects with
 * `TRACE_CANCELLED`. A file that is no picture rejects with `IMAGE_UNREADABLE`. A background
 * thread that fails or stops answering is given up on, and the picture read on the page instead:
 * slower to watch, the same result — except a TIFF the thread had begun to open, which is not
 * opened again on the page, where a scan too large for the thread would freeze it. It never
 * leaves the caller waiting.
 */
export const readPictureInBackground = (file: Blob, reading: ReadingOptions = {}): Promise<PictureReading> => {
  const {
    degrees = 0, signal, onProgress, options = {},
  } = reading;

  if (signal?.aborted) return Promise.reject(new Error(TRACE_CANCELLED));

  /** Stops here if nobody wants the result any more: checked after every wait. */
  const stillWanted = (): void => {
    if (signal?.aborted) throw new Error(TRACE_CANCELLED);
  };

  /** Everything on the page: how it is done without a background thread. */
  const onThePage = async (): Promise<PictureReading> => {
    onProgress?.(progressOf('opening', 1));
    const image = await pageImaging.load(file);
    stillWanted();
    const grey = pageImaging.grey(image, degrees);
    stillWanted();
    const { sketch, ink } = traceDrawing(inkOf(grey, options.config), grey.width, grey.height, {
      ...options,
      onStage: (stage, pass): void => onProgress?.(progressOf(stage, pass)),
    });
    const previewUrl = pageImaging.preview(image, degrees);
    stillWanted();
    return {
      sketch, ink, previewUrl, width: grey.width, height: grey.height, timings: {},
    };
  };

  const worker = startWorker();
  if (!worker) return onThePage();

  // What the file really is, whatever it is called: known before the thread can fail.
  const format = headOf(file).then(pictureFormatOf, (): PictureFormat => 'unknown');

  return new Promise((resolve, reject) => {
    let settled = false;
    let timer: number | undefined;
    let preview: Promise<string> = Promise.resolve('');
    let size = { width: 0, height: 0 };
    // Whether the thread is opening the file itself: until it says it cannot.
    let threadOpens = true;

    const finish = (outcome: () => PictureReading | Promise<PictureReading>): void => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      worker.terminate();
      // eslint-disable-next-line no-use-before-define -- a function, declared below: finish and abandon call each other
      signal?.removeEventListener('abort', abandon);
      Promise.resolve().then(outcome).then(resolve, reject);
    };

    function abandon(): void {
      finish(() => { throw new Error(TRACE_CANCELLED); });
    }

    /**
     * Read on the page after the thread failed or fell silent — but not a TIFF: the thread was
     * already opening it, and whatever stopped it there would stop the page too.
     */
    const fallBack = (reason: string): void => finish(async () => {
      if (threadOpens && await format === 'tiff') throw new PictureUnreadable('tiff', reason);
      stillWanted();
      return onThePage();
    });

    // Given up on after a silence, not after a set time: it says something at every step.
    const listen = (): void => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => fallBack('the background thread stopped answering'), TRACE_SILENCE_MS);
    };

    const post = (request: TraceRequest, transfer: Transferable[] = []): void => {
      listen();
      worker.postMessage(request, transfer);
    };

    /** The page opens what the thread cannot, and hands the pixels over — moved, not copied. */
    const openHere = (said?: { format: PictureFormat; reason?: string }): void => {
      pageImaging.load(file).then(
        (image) => {
          if (settled) return;
          onProgress?.(progressOf('opening', 1));
          try {
            const grey = pageImaging.grey(image, degrees);
            size = { width: grey.width, height: grey.height };
            preview = Promise.resolve(pageImaging.preview(image, degrees));
            post({ kind: 'grey', image: grey, options }, [grey.pixels.buffer]);
          } catch (failure) {
            // The file opened; the page's canvas failed. That is no unreadable file.
            finish(() => { throw failure; });
          }
        },
        (failure) => finish(() => {
          if (failure instanceof PictureUnreadable) throw failure;
          throw new PictureUnreadable(said?.format ?? 'unknown', said?.reason);
        }),
      );
    };

    signal?.addEventListener('abort', abandon, { once: true });

    worker.onerror = (): void => fallBack('the background thread failed');
    worker.onmessage = (event: MessageEvent<TraceReply>): void => {
      if (settled) return;
      listen();
      const reply = event.data;

      switch (reply.type) {
        case 'progress':
          onProgress?.(progressOf(reply.stage, reply.reading));
          break;
        case 'prepared':
          size = { width: reply.width, height: reply.height };
          preview = reply.preview ? dataUrlOf(reply.preview) : Promise.resolve('');
          break;
        case 'done':
          finish(async () => ({
            sketch: reply.sketch, ink: reply.ink ?? null, previewUrl: await preview, ...size, timings: reply.timings,
          }));
          break;
        case 'unsupported':
          // No canvas of its own: the page opens the picture, a TIFF too, and the thread reads it.
          threadOpens = false;
          openHere();
          break;
        case 'unreadable':
          // A kind only the page's image element opens (an SVG; a HEIC photo in Safari) — but not a
          // TIFF: the thread's decoder is the page's, and it has already said no.
          if (reply.format === 'tiff') finish(() => { throw new PictureUnreadable('tiff', reply.reason); });
          else openHere(reply);
          break;
        case 'failed':
          fallBack(reply.message);
          break;
        default:
          fallBack('the background thread said something unexpected');
      }
    };

    post({
      kind: 'file', file, degrees, longestSide: TRACE_LONGEST_SIDE, options,
    });
  });
};

/**
 * A grey image traced into a sketch, on a background thread when the browser has one, or right
 * here when it has not, or when the thread fails or stops answering. The image is copied to the
 * thread, not moved, so it can still be read here if it has to be.
 *
 * `signal` stops it: the thread is ended and the promise rejects with `TRACE_CANCELLED`. A trace
 * that fails on its own rejects with its error; it never leaves the caller waiting.
 */
export const traceInBackground = (
  image: GreyImage,
  signal?: AbortSignal,
  given?: TraceOptions,
  onProgress?: (progress: ReadingProgress) => void,
): Promise<FloorPlanSketch> => {
  const options = given ?? {};
  const here = (): FloorPlanSketch => traceSketch(inkOf(image, options.config), image.width, image.height, options);

  if (signal?.aborted) {
    return Promise.reject(new Error(TRACE_CANCELLED));
  }

  const worker = startWorker();
  if (!worker) {
    return new Promise((resolve) => { resolve(here()); });
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    let timer: number | undefined;

    const finish = (outcome: () => FloorPlanSketch): void => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      worker.terminate();
      // eslint-disable-next-line no-use-before-define -- a function, declared below: finish and abandon call each other
      signal?.removeEventListener('abort', abandon);

      try {
        resolve(outcome());
      } catch (error) {
        reject(error);
      }
    };

    const listen = (): void => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => finish(here), TRACE_SILENCE_MS);
    };

    function abandon(): void {
      finish(() => { throw new Error(TRACE_CANCELLED); });
    }
    signal?.addEventListener('abort', abandon, { once: true });

    worker.onmessage = (event: MessageEvent<TraceReply>): void => {
      if (settled) return;
      listen();
      const reply = event.data;
      if (reply.type === 'progress') onProgress?.(progressOf(reply.stage, reply.reading));
      else if (reply.type === 'done') finish(() => reply.sketch);
      else finish(here);
    };
    worker.onerror = (): void => finish(here);

    listen();
    const request: TraceRequest = { kind: 'grey', image, options };
    worker.postMessage(request);
  });
};

/**
 * What nobody marked on a drawing, kept as drawn (`asDrawnShapesOf`) — on a background thread
 * when the browser has one, so the page does not stop while a large picture's outlines are
 * traced; right here when it has none, or the thread fails or stops answering. The ink is copied
 * to the thread, not handed over, so it can still be traced here if it has to be. It never
 * leaves the caller waiting, and never fails for want of a thread.
 */
export const asDrawnInBackground = (ink: SketchInk, sketch: FloorPlanSketch): Promise<SketchShape[]> => {
  const here = (): SketchShape[] => asDrawnShapesOf(ink, sketch);
  const worker = startWorker();
  if (!worker) {
    return new Promise((resolve) => { resolve(here()); });
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = (shapes: () => SketchShape[]): void => {
      if (settled) return;
      settled = true;
      // eslint-disable-next-line no-use-before-define -- declared below: finish and the timer refer to each other
      window.clearTimeout(timer);
      worker.terminate();
      resolve(shapes());
    };
    const timer = window.setTimeout(() => finish(here), TRACE_SILENCE_MS);

    worker.onmessage = (event: MessageEvent<TraceReply>): void => {
      const reply = event.data;
      if (reply.type === 'as-drawn') finish(() => [...reply.shapes]);
      else if (reply.type !== 'progress') finish(here);
    };
    worker.onerror = (): void => finish(here);

    const request: TraceRequest = { kind: 'as-drawn', ink, sketch };
    worker.postMessage(request);
  });
};
